<template>
  <div class="score-distribution-chart">
    <div v-if="!hasData" class="no-data-tip">暂无数据</div>
    <div v-else id="scoreDistributionChart" ref="chartRef" class="chart-content"></div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, onMounted, watch, nextTick, computed } from 'vue';
import * as echarts from 'echarts';

const props = defineProps({
  data: {
    type: Array,
    default: () => []
  }
});

const chartRef = ref(null);
let chartInstance = null;

// 计算属性判断是否有数据
const hasData = computed(() => {
  return Array.isArray(props.data) && props.data.length > 0 && 
         props.data.some(item => item && typeof item.value === 'number' && item.value > 0);
});

// 初始化图表
const initChart = () => {
  // 确保DOM元素已存在
  if (!chartRef.value) {
    console.warn('图表DOM元素不存在');
    return;
  }
  
  // 销毁已存在的图表实例
  if (chartInstance) {
    chartInstance.dispose();
  }
  
  // 如果没有数据，不创建图表实例
  if (!hasData.value) {
    console.log('没有有效数据，不渲染图表');
    return;
  }
  
  try {
    // 创建图表实例
    chartInstance = echarts.init(chartRef.value);
    
    // 准备图表数据
    const chartData = props.data.filter(item => item && typeof item.value === 'number');
    
    const option = {
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)'
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        data: chartData.map(item => item.name || '')
      },
      series: [
        {
          name: '成绩分布',
          type: 'pie',
          radius: '70%',
          center: ['50%', '50%'],
          data: chartData,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          label: {
            show: false
          },
          labelLine: {
            show: false
          }
        }
      ],
      color: ['#52c41a', '#1890ff', '#faad14', '#ff4d4f']
    };
    
    // 设置图表选项
    chartInstance.setOption(option);
    
    // 调整图表大小
    chartInstance.resize();
    
    console.log('图表初始化成功，数据项数量:', chartData.length);
  } catch (error) {
    console.error('图表初始化失败:', error);
  }
};

// 主动触发图表重绘的方法，供父组件调用
const triggerRedraw = () => {
  nextTick(() => {
    if (chartInstance) {
      chartInstance.resize();
    } else {
      // 如果实例不存在，重新初始化
      initChart();
    }
  });
};

// 暴露方法给父组件
defineExpose({
  triggerRedraw
});

// 监听数据变化
watch(
  () => props.data,
  () => {
    nextTick(() => {
      initChart();
    });
  },
  { deep: true }
);

// 处理窗口大小变化
const handleResize = () => {
  if (chartInstance) {
    chartInstance.resize();
  }
};

onMounted(() => {
  nextTick(() => {
    initChart();
    window.addEventListener('resize', handleResize);
  });
});

// 组件卸载时清理资源
import { onUnmounted } from 'vue';
onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  if (chartInstance) {
    chartInstance.dispose();
    chartInstance = null;
  }
});
</script>

<style scoped>
.score-distribution-chart {
  width: 100%;
  height: 400px;
  display: flex;
  flex-direction: column;
  position: relative;
}
.chart-content {
  width: 100%;
  height: 100%;
}
.no-data-tip {
  width: 100%;
  height: calc(100% - 30px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
  background-color: #fafafa;
  border-radius: 4px;
}
</style>